/**
 * Display specific utility functions
 * Made global via NGX.Util and used internally via import
 */

import _ from 'underscore';
import htmlTruncate from 'html-truncate';

// Import Google's crypto md5
import CryptoJS from 'crypto-js';

// Import list of SDK's
import sdks from '../providers/sdks';

import enums from '../../common/config/enums';

function loadScript(url, sdk) {
  if (!url) return false;
  var js,
    firstJSTag = document.getElementsByTagName('script')[0],
    sdkID = 'sdk-' + sdk;

  if (document.getElementById(sdkID)) return;

  js = document.createElement('script');
  js.id = sdkID;
  js.src = url;
  js.async = true;
  js.defer = true;
  js.onload = function() {
    NGX.App.Events.trigger(sdk + ':loaded');
    if (sdks[sdk].event) {
      NGX.App.Events.trigger(sdks[sdk].event);
    }
  };

  return firstJSTag.parentNode.insertBefore(js, firstJSTag);
}

/**
 * Video specific utils
 * TODO [SG]: Sync isVine and isInstagram with others to return the id's of the videos
 */

export const video = {
  isYoutube: function(url) {
    function youtube_parser(url) {
      var p = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
      return url.match(p) ? RegExp.$1 : false;
    }
    return youtube_parser(url) || false;
  },

  isVimeo: function(url) {
    var regExp = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    var match = url.match(regExp) ? RegExp.$3 : false;
    return match;
  },

  isVine: function(url) {
    //e.g. https://vine.co/v/bThFt1qVJnW
    return url.indexOf('vine.co/v/') > -1;
  },

  isInstagram: function(url) {
    return url.indexOf('instagram.com/') > -1;
  },
};

/**
 * Asynchronously loads an SDK if it's not already loaded
 * @param {string} sdk
 * @param {Function} [callback]
 */

export function requireSDK(sdk, callback, locale) {
  if (NGX.App.state.loadedSDKs.indexOf(sdk) > -1) {
    if (callback && 'function' === typeof callback) callback.call(this);
  } else {
    log.debug('SDK [' + sdk + '] not loaded, adding asynchronously', 6);
    var _sdk = sdks[sdk];
    if (callback && 'function' === typeof callback) NGX.App.Events.once(sdk + ':ready', callback);
    if (_sdk.cbName) {
      window[_sdk.cbName] = _sdk.cb;
    }

    // Grab the SDK url.
    var url = _sdk.url;

    // Substitute any known tokens in the url >

    // Locale.
    var _locale = locale || NGX.context.locale;

    // If no locale from either of the above places.
    if (!_locale) {
      // Check if sdk has defined a default locale.
      if (_sdk.defaultLocale) {
        _locale = _sdk.defaultLocale;
        // Otherwise fallback to something sensible.
      } else {
        _locale = 'en_US';
      }
    }
    url = url.replace('{locale}', _locale);

    if (loadScript(url, sdk)) {
      NGX.App.state.loadedSDKs.push(sdk);
    }
  }
}

/**
 * MD5's a string via Google's CryptoJS
 * @param {string} string
 * @returns {string}
 */
export function hashCode(string) {
  return CryptoJS.MD5(string).toString();
}

/**
 * Show a small warning modal when users attempt to enter/vote via preview
 */
export function previewWarning() {
  NGX.overlay.create({
    content: '<p class="xCopy">This action is unavailable in Preview mode</p>',
  });
}

/**
 * Checks validity of an NGX Asset ref
 * @param ref
 * @returns {*|boolean}
 */
export function isAssetRef(ref) {
  if (typeof ref !== 'string' || ref.length < 1) return false;
  return (
    ref &&
    /^(ugc|bundle|asset|weburl|ext|youtube|vimeo|facebook|slideshare|vine|bazaarvoice|flickr|googleplus|instagram|linkedin|lithium|pinterest|twitter|vk|newscred):/.test(
      ref
    )
  );
}

/**
 * Inner function. It checks whether the provided param is the formatting part of the assetRef.
 * It is made as a quick fix for WYN-8037.
 * Formatting part looks like this: position=center&size=cover...
 * @param {string} assetFormatting String with fotmatting part.
 * @returns The result of splitting, or empty array if it is not formatting row
 */
const getAssetFormatting = (assetFormatting) => {
  const splitted = assetFormatting.split('&');
  let result = true;
  /**
   * The formatting part is a string separated by ‘&', which contains key-value pairs, separated by ‘=’.
   * We could have the situation when the value is empty: size=cover&color=.
   * To check this, I split the row by '&’, and check each part with the regex template.
   * Regex: we need to add '-' and '#', because we could have values as "repeat=repeat-xy",
   * or "color=#FF0000".
   */
  const regex = /^[\w-]+=([\w-#]+){0,1}$/;
  for (const part of splitted) {
  	result = result && regex.test(part);
  }
  return result ? splitted : [];
};

/**
 * Create an asset object from its assetRef
 * @param {string} assetRef
 * @param {object} options
 * @returns {Object}
 */
export function assetFromRef(assetRef, options = {}) {
  if (typeof assetRef === 'string' && assetRef.indexOf(':') > -1) {
    const splittedAssetRef = assetRef.split(':');
    let [service, type, dimensions, assetLocation] = splittedAssetRef;
    // We could be 100% sure that if the formatting part exists - it would be added at the end of the assetRef.
    // Because of this, we take the last part as the formatting config, and will check it below.
    const formattingConfig = splittedAssetRef[splittedAssetRef.length - 1];

    const _assetPrefix = options.assetUrl || NGX.context.assetUrl || '/';
    const _ugcPrefix = options.ugcUrl || NGX.context.ugcAssetPath || '/';

    const formatting = {};
    if (formattingConfig) {

      const formattingParts = getAssetFormatting(formattingConfig);

      /**
       * Check whether the last part contains formatting. If yes - we will join 4th and subsequent parts of
       * assetRef, besides the last one. Otherwise - we will just join the 4th and subsequent parts.
       * This would be a resource address.
       */
      if (formattingParts.length > 0) {
        for (const formattingPart of formattingParts) {
          let [formatKey, formatValue] = formattingPart.split('=');
          if (formatValue) {
            if (formatValue === 'repeat-xy') {
              formatValue = 'repeat';
            }
            formatting[formatKey] = formatValue;
          }
        }
        assetLocation = splittedAssetRef.slice(3, -1).join(':');
      } else {
        assetLocation = splittedAssetRef.slice(3).join(':');
      }
    }

    let src;
    if (service == 'asset' || service == 'bundle') {
      src = _assetPrefix + assetLocation;
    } else if (service == 'ugc') {
      src = _ugcPrefix + assetLocation;
    } else {
      src = assetLocation || '';
    }

    if (!!dimensions && dimensions !== 'auto') {
      const [width, height] = dimensions.split('x');
      dimensions = {
        width,
        height
      };
    }


    return {
      type:      service,
      assetType: type,
      size:      dimensions,
      path:      assetLocation,
      src:       src,
      formatting
    };
  }
  return false;
}

/**
 * Return an asset's path from its ref
 * @param {string} assetRef
 * @returns {string}
 */

export function assetPathFromRef(assetRef) {
  var asset = assetFromRef(assetRef, NGX.context);
  return asset.src || '';
}

/**
 * Call the callback after user is successfully authenticated with Facebook
 * @param {Function} callback
 * @param {object} options
 */

export function fbLogin(callback, options) {
  requireSDK(log.getDebugLevel() ? 'fbdebug' : 'fb', function() {
    var perms = NGX.App.config.permissionRequest;
    // call login & ask for permissionRequest.timing
    if ((perms && perms.timing.indexOf(options.timing) > -1) || options.timing === 'now') {
      // FB.getLoginStatus is faster than letting FB.login do it, and doesn't flicker the screen.
      FB.getLoginStatus(function(response) {
        // if connected and additional permissions are not required
        if (response.status === 'connected' && (perms.scope === '' || options.scope === '')) {
          // update the userSession data, if the user is connected now
          // We should do this here (as we do below), because in case if we receive the response from FB that
          // the user is authorized, we will just call the callback, and our session data will remain old
          if (NGX.App.state) {
            NGX.App.state.currentUser = NGX.App.state.currentUser || {};
            NGX.App.state.currentUser.userSession = response;
          }
          callback.apply(response, arguments);
        } else {
          // otherwise takeoff and login the app from orbit. its the only way to be sure.
          FB.login(
            function(response) {
              // update the userSession data on login
              if (response.status === 'connected' && NGX.App.state) {
                NGX.App.state.currentUser = NGX.App.state.currentUser || {};
                NGX.App.state.currentUser.userSession = response;
              }
              if (typeof callback === 'function') {
                callback.apply(response, arguments);
              }
            },
            { scope: perms.scope }
          );
        }
      });
      // catchall for campaigns that don't have permissions defined
    } else if (typeof callback === 'function') {
      if (options.timing == 'init') {
        FB.getLoginStatus(function(response) {
          callback.apply(response, arguments);
        });
      } else {
        callback.apply(arguments);
      }
    }
  });
}

/**
 * Resizes Facebook canvas/iframe
 * @param {object} opt
 */
export function resizeCanvas(opt) {
  if (typeof FB !== 'undefined' && !NGX.context.isPreview) {
    var defaults = {
        strategy: 'auto',
        height:   800,
      },
      options = _.extend(defaults, opt);

    switch (options.strategy) {
      case 'auto':
        // by calling auto, then manually setting the height, we can force the tab to fit the content
        FB.Canvas.setAutoGrow(true);
        FB.Canvas.setSize({ height: 480 });
        break;
      case 'explicit':
        FB.Canvas.setSize({ height: options.height });
        break;
      case 'calculate':
        $window.load(function() {
          var height = $body.outerHeight(true) + 20 > options.height ? $body.outerHeight(true) + 20 : options.height;
          FB.Canvas.setSize({ height: height });
        });
        break;
      default:
        FB.Canvas.setAutoGrow(true);
        break;
    }
  }
}

/**
 * Adds Referer user UUID to query string
 *
 * [WYN-6333] Renamed this function. It used to be called 'addRefCookie', which sounds like
 * it is adding a cookie, not getting a value from a cookie & appending it to a link...which
 * is what it actually does.
 *
 * Privacy Management: this function will check if the user has given consent for analytics cookies.
 *    - If so, it returns the link with the refferer uuid added.
 *    - If not, it returns the link unmodified.
 *
 * Refactored to use StorageService.
 *
 * @param {Object} config
 * @returns {string} Link with referrer ID appended as query string param, or as-is depending on privacy consents.
 */
export function appendReferrerID(config) {
  // If we can't do analytics cookies, don't look up the user UUID & don't append to query string.
  const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
  if (!allowCookies) {
    // Return link as-is.
    return (config && config.link) || '';
  }

  let outLink = '';
  if (config.link) {
    outLink = config.link;

    const value = NGX.StorageManager.get('ngx');
    // Append the UUID from the cookie as a parameter on the link
    if (value) {
      if (config.useAppData || config.link.indexOf('app/') !== -1) {
        if (config.link.indexOf('app_data') !== -1) {
          outLink += encodeURIComponent('&r=' + value);
        } else {
          outLink += '&app_data=' + encodeURIComponent('r=' + value);
        }
      } else {
        outLink += config.link.indexOf('?') !== -1 ? '&r=' + value : '?r=' + value;
      }
    }
    return outLink;
  }
}

/**
 * Cross origin request (cross browser)
 * @param {string} method
 * @param {string} url
 * @returns {global.XMLHttpRequest|XMLHttpRequest}
 */
export function createCORSRequest(method, url) {
  var xhr = new XMLHttpRequest();
  if ('withCredentials' in xhr) {
    xhr.open(method, url, true);
    xhr.withCredentials = true;
  } else if (typeof XDomainRequest !== 'undefined') {
    xhr = new XDomainRequest();
    xhr.open(method, url);
  } else {
    xhr = null;
  }
  return xhr;
}

/**
 * Filters out ALL html tags
 * @param text
 * @returns {string}
 */
export function stripHTMLTags(text) {
  var _div = document.createElement('div');
  _div.innerHTML = text;
  return _div.textContent || _div.innerText || '';
}

/**
 * Nasty old Countdown initialisation + template
 * @param {Object} config
 */
export function initialiseCountdown(config) {
  var untilDate = new Date(config.date);
  $(config.countdown).countdown({
    until:  untilDate,
    layout: config.layout ?
      config.layout :
      '{d<} <span id="daysWrapper"><span class="countdownValue" id="daysValue">{dn}</span><span class="countdownLabel" id="daysLabel">{dl}</span></span>{d>} {h<}<span id="hoursWrapper"><span class="countdownValue" id="hoursValue">{hn}</span><span class="countdownLabel" id="hoursLabel">{hl}</span></span>{h>} {m<} <span id="minutesWrapper"><span class="countdownValue" id="minutesValue">{mnn}</span><span class="countdownLabel" id="minutesLabel">{ml}</span></span>{m>} {s<}<span id="secondsWrapper"><span class="countdownValue" id="secondsValue">{snn}</span><span class="countdownLabel" id="secondsLabel">{sl}</span></span>{s>}',
    compact:       true,
    compactLabels: config.compactLabels ? config.compactLabels : ['y', 'm', 'w', 'd', 'h', 'm', 's'],
    onExpiry:      function() {
      NGX.Util.expireCountdown(config);
    },
    onTick:       NGX.Util.watchCountdown,
    alwaysExpire: true,
    format:       config.format ? config.format : 'dhmS',
  });
}

/**
 * Replace classes on countdown wrapper
 * @param config
 */
export function expireCountdown(config) {
  if (config.type == 'prelaunch') {
    document.location.href = config.expiryURL;
  }
  $(this)
    .parent()
    .removeClass('lastWeek lastDay lastHour lastMinute')
    .addClass('expired');
}

/**
 * Apply timeout warning classes to quiz countdowns
 * @param periods
 */
export function watchQuizCountdown(periods) {
  if (periods[5] == 0 && periods[6] <= 5) {
    $(this).addClass('lastFewSeconds');
  } else {
    $(this).removeClass('lastFewSeconds');
  }
}

/**
 * Replace countdown classes dependant on how close we are to the end
 * @param periods
 */
export function watchCountdown(periods) {
  // [0] is years, [1] is months, [2] is weeks, [3] is days, [4] is hours, [5] is minutes, and [6] is seconds.
  // $( this ).children().filter( ":first" ).addClass( 'big' );

  if (periods[0] == 0 && periods[1] == 0 && periods[2] == 0 && periods[3] < 7) {
    $(this)
      .parent()
      .removeClass('lastDay lastHour lastMinute')
      .addClass('lastWeek');
  }

  if (periods[0] == 0 && periods[1] == 0 && periods[2] == 0 && periods[3] == 0 && periods[4] <= 24) {
    $(this)
      .parent()
      .removeClass('lastWeek lastHour lastMinute')
      .addClass('lastDay');
  }

  if (periods[0] == 0 && periods[1] == 0 && periods[2] == 0 && periods[3] == 0 && periods[4] == 0 && periods[5] <= 60) {
    $(this)
      .parent()
      .removeClass('lastWeek lastDay lastMinute')
      .addClass('lastHour');
  }
  if (
    periods[0] == 0 &&
    periods[1] == 0 &&
    periods[2] == 0 &&
    periods[3] == 0 &&
    periods[4] == 0 &&
    periods[5] == 0 &&
    periods[6] <= 60
  ) {
    $(this)
      .parent()
      .removeClass('lastWeek lastDay lastHour')
      .addClass('lastMinute');
  }
}

/**
 * Capitalizes a string
 * @param {string} str - String to capitalize
 * @returns {string|boolean}
 */

export function capitalize(str) {
  if (!str) return false;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Capitalizes a string, but without lowercasing
 * @param {string} str - String to capitalize
 * @returns {string|boolean}
 */

export function UCFirst(str) {
  if (!str) return false;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * String token replacement. add more if required. call with an object's scope as a fallback, or pass object as a param. cannot be called without a parameter & using "window" as scope, because that would be stupid.
 * @param str : {string} The template string.
 * @param obj : {object} The object to search inside for replacement values.
 * @returns {string}
 */
export function tokenize(str, obj) {
  var match,
    // get the correct context
    context = obj || (this === window ? {} : this);
  if (typeof str === 'string') {
    // match any grails-style tokens: ${tokenName}
    while ((match = /\$\{(.*?)\}/g.exec(str))) {
      // replace the token with the matching value from the scoped object, using parenthesised substring from returned array & trim . eg. gets 'a' from '${ a }'
      str = str.replace(match[0], context[trim(match[1])] || '');
    }
    return str;
  }
  // don't break callers without type checks on the returned value.
  return '';
}

/**
 * Yet another String trim (added to remove a jQuery dependency)
 * @param {string} str : string to trim
 * @returns {string}
 * NOTE: Wrapped as a polyfill, the method returns native trim function if available
 */
export const trim = (function(nativeTrimSupport) {
  // wrap the native trim if present
  if (nativeTrimSupport)
    return function nativeTrim(str) {
      return 'string' === typeof str ? str.trim(str) : false;
    };
  // otherwise use the fastest trim we can come up with
  return function fastTrim(str) {
    if ('string' !== typeof str) return false;
    var len = str.length,
      checkCode = function(code) {
        var c = str.charCodeAt(code);
        return c != 32 && c != 10 && c != 13 && c != 9 && c != 12;
      };
    for (var i = 0; i < len; i++) {
      if (checkCode(i)) break;
    }
    for (var j = len - 1; j >= i; j--) {
      if (checkCode(j)) break;
    }
    return str.substring(i, j + 1);
  };
})('function' === typeof String.prototype.trim);

// App flow actions

/**
 * Shorthand app flow methods (return the function calls because they won't exist yet)
 * @param {string} ref - action ref
 * @returns {*}
 */
export function action(ref, ctxt) {
  return NGX.App.api.action(ref, ctxt);
}

/**
 * Quick switch state
 * @param {string} ref - state name
 */
export function state(ref, ctxt) {
  return NGX.App.api.state(ref, ctxt);
}

/**
 * Check if a string value matches ref structure
 * @param {string} str
 * @returns {*}
 */

export function isRef(str) {
  if (typeof str === 'string') {
    // first segment must be lowercase letters only, subsequent segments may contain any word character.
    return /[a-z]+(:\w+)+/g.test(str);
  }
  // return undefined if the str value is bad so we can test if we need to
  return undefined;
}

/**
 * Trigger a function asynchronously
 * Effectively takes it out of the call stack and executes at the first available moment
 * @param {Function} fn
 * @param {object} context
 * @param {integer} delay
 */

export function asyncFn(fn, context, delay) {
  if (typeof fn === 'function') {
    setTimeout(function() {
      fn.call(context || window);
    }, delay || 20);
  }
}

/**
 * Get StorageManager key based on campaign/tenant and control's name
 *
 * Privacy Management: this function is not privacy gated, if you use it somewhere, be sure
 * to check for the appropriate type of cookie consent before calling this!
 *
 * @param {object} cfg
 * @returns {string} the generated cookie key
 */
export function getStorageKey(cfg) {
  // 1: Build key from <account id||campaign id>_<control profile>
  var skey = [cfg.filterScope === 'campaign' ? NGX.App.config.id : NGX.App.config.tenant, cfg.controlName];

  // 2: if remember_me campaign, also add _<hashed email>
  var _rememberEmail = document.getElementById('xReturningUserEmail');
  if (_rememberEmail && _rememberEmail.value) {
    skey.unshift(NGX.Util.hashCode(_rememberEmail.value));
  }

  return NGX.StorageManager.getKey(skey);
}

/**
 * Gets YouTube Video ID from url
 * @param {string} path
 */

export function getYoutubeVideoId(path) {
  var patt = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
  try {
    return patt.exec(path)[1];
  } catch (e) {
    return '';
  }
}

/**
 * Convert a url encoded query string from a shallow object
 * @param {object} params
 * @returns {string}
 */

export function param(params) {
  var _encodedStr = '';
  for (const p in params) {
    if (params.hasOwnProperty(p)) {
      if (_encodedStr.length > 0) _encodedStr += '&';
    }
    _encodedStr += encodeURI(p + '=' + params[p]);
  }

  return _encodedStr;
}

/**
 * Adds items to current page's query string, or on fullUrl if given
 * @param  newParams: 		{object}	The object of params to add/replace
 * @param  replaceIfExists: {object}	Replace existing url params?
 * @param  fullUrl: 		{object}	Arbitrary full url
 * @returns {string}
 */
export function updateQueryString(newParams, replaceIfExists, fullUrl) {
  var _newParams = newParams || {},
    _replace = !!replaceIfExists === false ? false : true,
    _qUrl = (fullUrl === 'ctx:smarturl' ? NGX.context.smarturl : fullUrl) || document.location.href,
    _hasQS = _qUrl.indexOf('?') > -1 ? true : false,
    _qString = _hasQS ? _qUrl.substring(_qUrl.indexOf('?') + 1) : '',
    _qSObj = {},
    _newQSObj = {};

  if (_qString) {
    _.each(_qString.split('&'), function(item) {
      var kvp = item.split('=');
      _qSObj[kvp[0]] = kvp[1] || undefined;
    });
  }

  if (_replace) {
    _.extend(_newQSObj, _qSObj, _newParams);
  } else {
    _.extend(_newQSObj, _newParams, _qSObj);
  }

  if (_hasQS) _qUrl = _qUrl.substring(0, _qUrl.indexOf('?'));

  return _qUrl + '?' + decodeURIComponent(param(_newQSObj));
}

/** Translates a link to link + action where actionRef is passed
 * @param link: {string} link or actionRef
 * @returns {object} with href + data action
 */
export function translateHref(link) {
  if (!link) return;
  var _linkParts = link.split(':');
  var translated = {
    href:   link,
    action: null,
  };

  if (_linkParts.length == 1 || _linkParts[0].indexOf('http') === 0) return translated;

  translated.href = '#';
  if (_linkParts[0] === 'action') {
    translated.action = _linkParts.slice(1).join(':');
  } else {
    translated.action = _linkParts.join(':');
  }

  return translated;
}

/**
 * Truncates supplied string to the correct length based on template variant
 * @param string : {string} The string to truncate.
 * @param variant : {string} The template variant (s,m,l)
 * @returns {string}
 */
export function truncateStringForTemplateVariant(string, variant) {
  let _string = string;

  const SMALL_MAX = 140;
  const MEDIUM_MAX = 255;
  const LARGE_MAX = 512;

  const _truncString = function(string, charLimit) {
    return htmlTruncate(string, charLimit);
  };

  switch (variant) {
    case 's':
      if (_string.length > SMALL_MAX) {
        _string = _truncString(_string, SMALL_MAX);
      }
      break;

    case 'm':
      if (_string.length > MEDIUM_MAX) {
        _string = _truncString(_string, MEDIUM_MAX);
      }
      break;

    case 'l':
      if (_string.length > LARGE_MAX) {
        _string = _truncString(_string, LARGE_MAX);
      }
      break;
  }

  return _string;
}

/**
 * Attempt to retrieve a label from NGX.lang
 * @param  labelPath: {string} The label path.
 * @return {string}
 */
export function getLabel(labelPath) {
  const _label = NGX.lang.get(labelPath);

  return _label;
}

/**
 *
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string}
 */

export function formatUserName(firstName, lastName) {
  if (!firstName && !lastName) return '';

  if ((!firstName && !lastName) || (typeof firstName === 'object' || typeof lastName === 'object')) {
    return '';
  } else {
    // now you can do a simplistic replace on the tokens in the string
    var repStr = NGX.lang.user.nameFormat.toLowerCase();
    repStr = repStr.replace(new RegExp('{{firstname}}', 'gm'), NGX.Util.capitalize(firstName));
    repStr = repStr.replace(new RegExp('{{lastname}}', 'gm'), NGX.Util.capitalize(lastName));
  }

  return repStr;
}

/**
 * Returns string safe for React to render
 * @param content : {string}
 * @returns {object} : HTML sanitized for React
 */
export function sanitizeHTML(content) {
  return { __html: content };
}

/**
 * Returns a formatted date
 * @param theDate : {date}
 * @param format  : {string}
 * @returns {string}
 */
export function getRelativeDate(theDate = new Date(), format = 'D MMMM YYYY') {
  var _oneWeekAgo = moment().subtract(1, 'weeks');
  var _moment = moment(theDate);

  if (_moment.isBefore(_oneWeekAgo)) {
    return _moment.format(format);
  } else {
    return _moment.fromNow();
  }
}

/**
 * Returns a formatted date
 * @param theDate : {date}
 * @param format  : {string}
 * @returns {string}
 */
export function getDateTime(theDate = new Date(), format = 'DD MMMM YYYY HH:mm') {
  const dateTime = moment(theDate).format('DD MMMM YYYY HH:mm');

  return dateTime;
}

/**
 * Transpose string. Note - this will soon be replaced by server side transposing to hide entirely from client.
 * @param string  : {string}
 * @returns {string}
 */
export function transposeString(string) {
  let transposedString = '';

  const from = 'abcdefghijklmnopqrstuvwxyz0123456789=/{}_ABCDEFGHIJKLMNOPQRSTUVWXYZ#';
  const to = 'qazwsxedcrfvtgbyhnujmikolpPLOKIMJUNHYBGTVFRCDEXSWZAQ0192837465/=#}{_';

  for (const charIndex in string) {
    const char = string[charIndex];
    let replaceChar = char;
    const replaceIndex = from.indexOf(char);
    if (replaceIndex !== -1) {
      replaceChar = to.charAt(replaceIndex);
    }

    transposedString += replaceChar;
  }

  return transposedString;
}

/**
 * Base 64 encode a Unicode string.
 * @param string  : {string}
 * @returns {string}
 */
export function b64EncodeString(string) {
  return btoa(
    encodeURIComponent(string).replace(/%([0-9A-F]{2})/g, function(match, p1) {
      return String.fromCharCode('0x' + p1);
    })
  );
}
